import React, { useMemo, useState, useRef, useCallback } from 'react';
import { useBalances } from '../hooks/useBalances';
import useSWR, { mutate } from 'swr';
import apiClient from '../services/apiClient';
import { useTimezone, formatTimestampWithTimezone } from '../contexts/TimezoneContext';
import { TrendingUp, TrendingDown, RefreshCw, ChevronUp, ChevronDown, Copy, ExternalLink, Check } from 'lucide-react';
import { EXCHANGE_ACCOUNT_TYPES } from '../constants';
import './BalancesDashboard.css';

const fetcher = (url) => apiClient.get(url).then(res => res.data);

// Helper to shorten address
const shortenAddress = (addr, start = 6, end = 4) => {
  if (!addr || addr.length < start + end + 3) return addr;
  return `${addr.slice(0, start)}...${addr.slice(-end)}`;
};

// Exchange colors - Beach color palette distributed evenly
// deep ocean: #204ECF, vista blue: #83A5F2, crab: #F26749, butterscotch: #EA9836, sand dollar: #FCDED6
const EXCHANGE_COLORS = {
  binance: '#204ECF',      // Deep ocean (blue)
  okx: '#F26749',          // Crab (reddish-orange)
  bybit: '#EA9836',        // Butterscotch (golden-orange)
  hyperliquid: '#83A5F2',  // Vista blue (blue)
  gateio: '#FCDED6',       // Sand dollar (pale beige)
  kucoin: '#204ECF',       // Deep ocean (blue)
  woox: '#F26749',         // Crab (reddish-orange)
  bullish: '#EA9836',      // Butterscotch (golden-orange)
  treasury: '#83A5F2',     // Vista blue for treasury (blue)
};

// Asset colors - Beach palette distributed evenly
const ASSET_COLORS = {
  USDT: '#204ECF',         // Deep ocean (blue)
  USDC: '#F26749',         // Crab (reddish-orange)
  ETH: '#EA9836',          // Butterscotch (golden-orange)
  BTC: '#83A5F2',          // Vista blue (blue)
};

// Format currency values
const formatCurrency = (value, compact = false) => {
  if (value === undefined || value === null) return '$0';
  const num = parseFloat(value);
  if (isNaN(num)) return '$0';
  
  if (compact && Math.abs(num) >= 1000000) {
    return `$${(num / 1000000).toFixed(2)}M`;
  }
  if (compact && Math.abs(num) >= 1000) {
    return `$${(num / 1000).toFixed(0)}K`;
  }
  
  return `$${num.toLocaleString(undefined, { 
    minimumFractionDigits: 0, 
    maximumFractionDigits: 0 
  })}`;
};

// Format percentage
const formatPercent = (value) => {
  if (value === undefined || value === null) return '0%';
  const num = parseFloat(value);
  if (isNaN(num)) return '0%';
  const sign = num >= 0 ? '+' : '';
  return `${sign}${num.toFixed(2)}%`;
};

// Format token amounts
const formatToken = (value, compact = false) => {
  if (value === undefined || value === null) return '0';
  const num = parseFloat(value);
  if (isNaN(num)) return '0';
  
  if (compact) {
    return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
  return num.toLocaleString(undefined, { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
};

// Stats Card Component
function StatsCard({ title, value, subtitle, icon, trend, highlight }) {
  return (
    <div className={`stats-card ${highlight ? 'highlight' : ''}`}>
      <div className="stats-label">{title}</div>
      <div className="stats-value-row">
        {icon && <span className="stats-icon">{icon}</span>}
        <span className={`stats-value ${trend === 'positive' ? 'positive' : trend === 'negative' ? 'negative' : ''}`}>
          {value}
        </span>
      </div>
      {subtitle && (
        <div className={`stats-subtitle ${trend === 'positive' ? 'positive' : trend === 'negative' ? 'negative' : ''}`}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

// Double Donut Chart Component - Inner ring: Assets, Outer ring: Account Types
function DoubleDonutChart({ 
  assetData, // { USDT: { total: number, byAccountType: { spot: number, futures: number, ... } }, ... }
  colors, 
  size = 280, 
  title, 
  showTimestamp = false 
}) {
  // Group assets: USDT, USDC, ETH, Other
  const focusAssets = ['USDT', 'USDC', 'ETH'];
  const assetGroups = { USDT: 0, USDC: 0, ETH: 0, Other: 0 };
  const assetBreakdown = { USDT: {}, USDC: {}, ETH: {}, Other: {} };
  
  Object.entries(assetData || {}).forEach(([asset, data]) => {
    const assetKey = focusAssets.includes(asset) ? asset : 'Other';
    assetGroups[assetKey] += data.total || 0;
    if (data.byAccountType) {
      Object.entries(data.byAccountType).forEach(([accountType, value]) => {
        if (!assetBreakdown[assetKey][accountType]) {
          assetBreakdown[assetKey][accountType] = 0;
        }
        assetBreakdown[assetKey][accountType] += value || 0;
      });
    }
  });
  
  const innerData = Object.entries(assetGroups)
    .filter(([_, value]) => value > 0)
    .map(([name, value]) => ({ name, value }));
  
  const total = innerData.reduce((sum, item) => sum + item.value, 0);
  if (total === 0) {
    return (
      <div className="donut-chart-wrapper">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="donut-svg">
          <text x={size/2} y={size/2 - 10} textAnchor="middle" fill="#fff" fontSize="14" fontWeight="500">
            {title || 'Total'}
          </text>
          <text x={size/2} y={size/2 + 10} textAnchor="middle" fill="#888" fontSize="12">
            $0
          </text>
        </svg>
      </div>
    );
  }
  
  const innerStrokeWidth = 25;
  const outerStrokeWidth = 15;
  const innerRadius = (size - innerStrokeWidth - outerStrokeWidth * 2) / 2;
  const outerRadius = innerRadius + (innerStrokeWidth + outerStrokeWidth) / 2;
  const innerCircumference = 2 * Math.PI * innerRadius;
  const outerCircumference = 2 * Math.PI * outerRadius;
  const centerX = size / 2;
  const centerY = size / 2;
  
  const formatTotal = (value) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '$0';
    if (num >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `$${(num / 1000).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };
  
  const accountTypeColors = {
    spot: '#4a9eff',
    futures: '#f26749',
    margin: '#ea9836',
    funding: '#83a5f2',
    main: '#83a5f2',
    trade: '#4a9eff',
    perp: '#f26749',
    unified: '#fcded6',
  };
  
  // Calculate inner ring segments
  let innerOffset = 0;
  const innerSegments = innerData.map((item) => {
    const percentage = item.value / total;
    const strokeDasharray = `${innerCircumference * percentage} ${innerCircumference * (1 - percentage)}`;
    const strokeDashoffset = -innerOffset;
    const startOffset = innerOffset;
    innerOffset += innerCircumference * percentage;
    
    const assetColor = colors[item.name] || ASSET_COLORS[item.name] || ['#204ECF', '#83A5F2', '#F26749', '#EA9836'][innerData.indexOf(item) % 4];
    
    return {
      name: item.name,
      value: item.value,
      percentage,
      strokeDasharray,
      strokeDashoffset,
      startOffset,
      color: assetColor,
    };
  });
  
  // Calculate outer ring segments - account types within each asset
  // Outer segments must align with inner segments
  const outerSegments = [];
  let cumulativeOuterOffset = 0;
  
  innerSegments.forEach((innerSegment) => {
    const assetBreakdownData = assetBreakdown[innerSegment.name] || {};
    const accountTypeEntries = Object.entries(assetBreakdownData)
      .filter(([_, value]) => value > 0)
      .sort(([_, a], [__, b]) => b - a);
    
    // Calculate the outer segment offset to align with inner segment start
    const outerSegmentStartOffset = (innerSegment.startOffset / innerCircumference) * outerCircumference;
    
    if (accountTypeEntries.length === 0) {
      // No account type breakdown, show full segment in asset color (lighter)
      const outerSegmentLength = outerCircumference * innerSegment.percentage;
      outerSegments.push({
        asset: innerSegment.name,
        accountType: null,
        strokeDasharray: `${outerSegmentLength} ${outerCircumference - outerSegmentLength}`,
        strokeDashoffset: -outerSegmentStartOffset,
        color: innerSegment.color,
        opacity: 0.3,
      });
    } else {
      // Calculate account type segments within this asset segment
      const assetTotal = assetData[innerSegment.name]?.total || 0;
      let accountTypeOffset = 0;
      
      accountTypeEntries.forEach(([accountType, value]) => {
        const accountTypePercentage = assetTotal > 0 ? value / assetTotal : 0;
        const outerSegmentLength = outerCircumference * innerSegment.percentage * accountTypePercentage;
        const remainingLength = outerCircumference - outerSegmentLength;
        
        outerSegments.push({
          asset: innerSegment.name,
          accountType,
          strokeDasharray: `${outerSegmentLength} ${remainingLength}`,
          strokeDashoffset: -(outerSegmentStartOffset + accountTypeOffset),
          color: accountTypeColors[accountType] || innerSegment.color,
          opacity: 0.8,
        });
        accountTypeOffset += outerSegmentLength;
      });
    }
  });
  
  // Collect unique account types from outer segments for legend
  const accountTypeLabels = {
    spot: 'Spot',
    perp: 'Perp',
    futures: 'Futures',
    margin: 'Margin',
    funding: 'Funding',
    main: 'Main',
    trade: 'Trade',
    unified: 'Unified',
  };
  
  const usedAccountTypes = new Set();
  outerSegments.forEach(segment => {
    if (segment.accountType) {
      usedAccountTypes.add(segment.accountType);
    }
  });
  
  const accountTypeLegend = Array.from(usedAccountTypes).map(accountType => ({
    accountType,
    label: accountTypeLabels[accountType] || accountType,
    color: accountTypeColors[accountType] || '#666',
  }));
  
  return (
    <div className="double-donut-container">
      <div className="donut-chart-wrapper">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="donut-svg">
          {/* Inner ring - Assets */}
          {innerSegments.map((segment) => (
            <circle
              key={`inner-${segment.name}`}
              cx={centerX}
              cy={centerY}
              r={innerRadius}
              fill="none"
              stroke={segment.color}
              strokeWidth={innerStrokeWidth}
              strokeDasharray={segment.strokeDasharray}
              strokeDashoffset={segment.strokeDashoffset}
              transform={`rotate(-90 ${centerX} ${centerY})`}
              style={{ transition: 'stroke-dasharray 0.3s ease' }}
            />
          ))}
          
          {/* Outer ring - Account Types per Asset */}
          {outerSegments.map((segment, index) => (
            <circle
              key={`outer-${segment.asset}-${segment.accountType || 'none'}-${index}`}
              cx={centerX}
              cy={centerY}
              r={outerRadius}
              fill="none"
              stroke={segment.color}
              strokeWidth={outerStrokeWidth}
              strokeDasharray={segment.strokeDasharray}
              strokeDashoffset={segment.strokeDashoffset}
              transform={`rotate(-90 ${centerX} ${centerY})`}
              style={{ transition: 'stroke-dasharray 0.3s ease', opacity: segment.opacity }}
            />
          ))}
          
          {/* Center text */}
          <text x={centerX} y={centerY - 20} textAnchor="middle" fill="#fff" fontSize="12" fontWeight="500">
            {title || 'Total'}
          </text>
          <text x={centerX} y={centerY + 5} textAnchor="middle" fill="#fff" fontSize="24" fontWeight="600">
            {formatTotal(total)}
          </text>
        </svg>
      </div>
      
      {/* Legend for double donut chart */}
      <div className="double-donut-legend">
        {/* Inner ring legend - Assets */}
        <div className="legend-section">
          <div className="legend-section-title">Assets (Inner Ring)</div>
          <div className="legend-items">
            {innerSegments.map((segment) => (
              <div key={`legend-inner-${segment.name}`} className="legend-item">
                <span 
                  className="legend-dot legend-dot-large" 
                  style={{ backgroundColor: segment.color }} 
                />
                <span className="legend-label">{segment.name}</span>
                <span className="legend-value mono">{formatTotal(segment.value)}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Outer ring legend - Account Types */}
        {accountTypeLegend.length > 0 && (
          <div className="legend-section">
            <div className="legend-section-title">Account Types (Outer Ring)</div>
            <div className="legend-items">
              {accountTypeLegend.map((item) => (
                <div key={`legend-outer-${item.accountType}`} className="legend-item">
                  <span 
                    className="legend-dot legend-dot-small" 
                    style={{ backgroundColor: item.color, opacity: 0.8 }} 
                  />
                  <span className="legend-label">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Donut Chart Component with Center Label
function DonutChart({ data, colors, size = 200, title, showTimestamp = true }) {
  const validData = data.filter(item => item.value > 0);
  const total = validData.reduce((sum, item) => sum + item.value, 0);
  const strokeWidth = 30;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const centerX = size / 2;
  const centerY = size / 2;
  
  let currentOffset = 0;
  
  // Format total value
  const formatTotal = (value) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '$0';
    
    if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
      return `$${(num / 1000).toFixed(2)}K`;
    }
    return `$${num.toFixed(2)}`;
  };

  // Get current timestamp
  const getTimestamp = () => {
    const now = new Date();
    const day = now.getDate();
    const month = now.toLocaleDateString('en-US', { month: 'short' });
    const year = now.getFullYear();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `As of ${day} ${month} ${year}, ${hours}:${minutes}`;
  };
  
  if (total === 0) {
    return (
      <div className="donut-chart-wrapper">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="donut-svg">
          <circle
            cx={centerX}
            cy={centerY}
            r={radius}
            fill="none"
            stroke="#2a2a2a"
            strokeWidth={strokeWidth}
          />
          <text x={centerX} y={centerY - 10} textAnchor="middle" fill="#fff" fontSize="14" fontWeight="500">
            {title || 'Total'}
          </text>
          <text x={centerX} y={centerY + 10} textAnchor="middle" fill="#888" fontSize="12">
            $0
          </text>
        </svg>
      </div>
    );
  }
  
  return (
    <div className="donut-chart-wrapper">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="donut-svg">
        {/* Background circle */}
        <circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill="none"
          stroke="#1a1a1a"
          strokeWidth={strokeWidth}
        />
        
        {/* Segments */}
        {validData.map((item, index) => {
          const percentage = item.value / total;
          const strokeDasharray = `${circumference * percentage} ${circumference * (1 - percentage)}`;
          const strokeDashoffset = -currentOffset;
          currentOffset += circumference * percentage;
          
          // Fallback beach palette - all colors distributed evenly
          const beachFallbacks = ['#204ECF', '#83A5F2', '#F26749', '#EA9836', '#FCDED6'];
          const fallbackColor = beachFallbacks[index % beachFallbacks.length];
          
          return (
            <circle
              key={index}
              cx={centerX}
              cy={centerY}
              r={radius}
              fill="none"
              stroke={colors[item.name] || colors[index] || fallbackColor}
              strokeWidth={strokeWidth}
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              transform={`rotate(-90 ${centerX} ${centerY})`}
              style={{ transition: 'stroke-dasharray 0.3s ease' }}
            />
          );
        })}
        
        {/* Center text */}
        <text x={centerX} y={centerY - 20} textAnchor="middle" fill="#fff" fontSize="12" fontWeight="500">
          {title || 'Total Portfolio'}
        </text>
        <text x={centerX} y={centerY + 5} textAnchor="middle" fill="#fff" fontSize="24" fontWeight="600">
          {formatTotal(total)}
        </text>
        {showTimestamp && (
          <text x={centerX} y={centerY + 25} textAnchor="middle" fill="#888" fontSize="10">
            {getTimestamp()}
          </text>
        )}
      </svg>
    </div>
  );
}

// Simple Bar Chart for P&L
function PnLBarChart({ data }) {
  // Handle empty or no data case
  if (!data || data.length === 0) {
    return (
      <div className="pnl-chart empty">
        <div className="no-data-message">
          <span>No P&L data available</span>
          <span className="no-data-hint">Balance snapshots needed to calculate P&L</span>
        </div>
      </div>
    );
  }
  
  const maxValue = Math.max(...data.map(d => Math.abs(d.value)), 1); // Min 1 to avoid division by zero
  
  return (
    <div className="pnl-chart">
      {data.map((item, index) => (
        <div key={index} className="pnl-row">
          <span className="pnl-label">{item.label}</span>
          <div className="pnl-bar-container">
            <div 
              className={`pnl-bar ${item.value >= 0 ? 'positive' : 'negative'}`}
              style={{ width: `${(Math.abs(item.value) / maxValue) * 100}%` }}
            />
          </div>
          <span className={`pnl-value ${item.value >= 0 ? 'positive' : 'negative'}`}>
            {item.value >= 0 ? '+' : ''}{formatCurrency(item.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

// Balance History Chart with individual exchange lines and total portfolio
function BalanceHistoryChart({ timeRange, onRangeChange }) {
  const days = DAYS_MAP[timeRange] || 30;
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isExpanded, setIsExpanded] = useState(false); // Hidden by default
  
  const { data: historyData, error } = useSWR(
    `/analytics/balance-history?days=${days}`,
    fetcher,
    { refreshInterval: 60000 }
  );

  const chartData = useMemo(() => {
    if (!historyData?.snapshots || historyData.snapshots.length === 0) {
      return { exchanges: [], total: [], dates: [], minValue: 0, maxValue: 0 };
    }

    const snapshots = historyData.snapshots.sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );

    // Extract all unique exchanges from locationTotals
    const exchangeSet = new Set();
    snapshots.forEach(s => {
      if (s.locationTotals) {
        Object.keys(s.locationTotals).forEach(loc => {
          if (loc !== 'treasury') {
            exchangeSet.add(loc);
          }
        });
      }
    });
    const exchanges = Array.from(exchangeSet);

    // Build data series for each exchange
    const exchangeSeries = exchanges.map(exchange => ({
      name: exchange,
      color: EXCHANGE_COLORS[exchange.toLowerCase()] || '#83A5F2',
      values: snapshots.map(s => {
        const total = s.locationTotals?.[exchange]?.totalBalanceUsd || 0;
        return typeof total === 'string' ? parseFloat(total) : total;
      })
    }));

    // Build total portfolio series
    const totalSeries = snapshots.map(s => 
      typeof s.totalBalanceUsd === 'string' ? parseFloat(s.totalBalanceUsd) : s.totalBalanceUsd
    );

    // Get dates for x-axis
    const dates = snapshots.map(s => new Date(s.date));

    // Calculate min/max for scaling
    const allValues = [
      ...totalSeries,
      ...exchangeSeries.flatMap(e => e.values)
    ].filter(v => !isNaN(v) && v > 0);
    
    const minValue = Math.min(...allValues, 0);
    const maxValue = Math.max(...allValues, minValue * 1.1 || 1000);

    return {
      exchanges: exchangeSeries,
      total: totalSeries,
      dates,
      minValue,
      maxValue
    };
  }, [historyData]);

  const { exchanges, total, dates, minValue, maxValue } = chartData;
  const range = maxValue - minValue || 1;
  const chartHeight = 100;
  const chartWidth = 100;
  const paddingTop = 10;
  const paddingBottom = 15;

  // Convert values to SVG coordinates
  const valueToY = (value) => {
    return chartHeight - paddingBottom - ((value - minValue) / range) * (chartHeight - paddingTop - paddingBottom);
  };

  // Generate points for a series
  const generatePoints = (values) => {
    return values.map((value, i) => {
      const x = (i / (values.length - 1 || 1)) * chartWidth;
      const y = valueToY(value);
      return `${x},${y}`;
    }).join(' ');
  };

  // Format date for x-axis - include time for better visibility
  const formatDate = (date) => {
    const d = new Date(date);
    const month = d.toLocaleDateString('en-US', { month: 'short' });
    const day = d.getDate();
    const hours = d.getHours();
    const minutes = d.getMinutes();
    const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    return `${month} ${day} ${timeStr}`;
  };

  // Format date/time for tooltip (more detailed)
  const formatDateTime = (date) => {
    const d = new Date(date);
    const month = d.toLocaleDateString('en-US', { month: 'short' });
    const day = d.getDate();
    const year = d.getFullYear();
    const hours = d.getHours();
    const minutes = d.getMinutes();
    const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    return `${month} ${day}, ${year} ${timeStr}`;
  };

  // Generate x-axis labels (show more labels for better visibility)
  const xAxisLabels = useMemo(() => {
    if (dates.length === 0) return [];
    // Show more labels: ~8-10 for better date visibility
    const count = Math.min(Math.max(8, dates.length), dates.length);
    const step = Math.max(1, Math.floor(dates.length / (count - 1)));
    const labels = [];
    for (let i = 0; i < dates.length; i += step) {
      labels.push({ index: i, label: formatDate(dates[i]) });
    }
    if (labels[labels.length - 1]?.index !== dates.length - 1) {
      labels.push({ index: dates.length - 1, label: formatDate(dates[dates.length - 1]) });
    }
    return labels;
  }, [dates]);

  // Handle mouse move over chart
  const chartContainerRef = useRef(null);
  const handleMouseMove = (e) => {
    if (!chartContainerRef.current || dates.length === 0 || total.length === 0) return;
    
    const rect = chartContainerRef.current.getBoundingClientRect();
    // Get the SVG element (first child after y-axis)
    const svgElement = chartContainerRef.current.querySelector('.line-chart');
    if (!svgElement) return;
    
    const svgRect = svgElement.getBoundingClientRect();
    const x = e.clientX - svgRect.left;
    const percentX = (x / svgRect.width) * 100;
    
    // Find closest data point
    const dataIndex = Math.round((percentX / 100) * (dates.length - 1));
    const clampedIndex = Math.max(0, Math.min(dataIndex, dates.length - 1));
    
    if (clampedIndex >= 0 && clampedIndex < dates.length && clampedIndex < total.length) {
      setHoveredPoint({
        index: clampedIndex,
        date: dates[clampedIndex],
        balance: total[clampedIndex],
      });
      // Position tooltip above cursor, but keep it within viewport
      const tooltipX = e.clientX;
      const tooltipY = e.clientY - 10;
      setTooltipPosition({ x: tooltipX, y: tooltipY });
    }
  };

  const handleMouseLeave = () => {
    setHoveredPoint(null);
  };

  // Don't render on error
  if (error) {
    return null;
  }

  // Don't render anything if no history data
  if (!historyData?.snapshots || historyData.snapshots.length === 0) {
    return null;
  }

  return (
    <div className="balance-history-card">
      <div className="card-header">
        <div className="card-header-left">
          <h3>Balance History</h3>
          <button 
            onClick={() => setIsExpanded(!isExpanded)} 
            className="btn-chart-toggle"
            title={isExpanded ? 'Hide chart' : 'Show chart'}
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            <span>{isExpanded ? 'Hide' : 'Show'}</span>
          </button>
          {isExpanded && <span className="update-frequency">Updated hourly</span>}
        </div>
        {isExpanded && (
          <div className="time-range-toggle">
            {['1D', '7D', '30D', '90D'].map(range => (
              <button
                key={range}
                className={`range-btn ${timeRange === range ? 'active' : ''}`}
                onClick={() => onRangeChange(range)}
              >
                {range}
              </button>
            ))}
          </div>
        )}
      </div>
      {isExpanded && (
        <>
          <div 
            className="chart-container" 
            ref={chartContainerRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
              <div className="chart-y-axis">
                <span>{formatCurrency(maxValue, true)}</span>
                <span>{formatCurrency((maxValue + minValue) / 2, true)}</span>
                <span>{formatCurrency(minValue, true)}</span>
              </div>
              <svg className="line-chart" viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="none">
              <defs>
                <linearGradient id="totalGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#204ECF" stopOpacity="0.2" />
                  <stop offset="100%" stopColor="#204ECF" stopOpacity="0.05" />
                </linearGradient>
              </defs>
              
              {/* Exchange lines */}
              {exchanges.map((exchange, idx) => {
                const points = generatePoints(exchange.values);
                return (
                  <polyline
                    key={exchange.name}
                    points={points}
                    fill="none"
                    stroke={exchange.color}
                    strokeWidth="0.3"
                    strokeOpacity="0.7"
                    vectorEffect="non-scaling-stroke"
                  />
                );
              })}
              
              {/* Total portfolio area (stacked) */}
              {total.length > 0 && (() => {
                const totalPoints = generatePoints(total);
                const areaPoints = `0,${chartHeight - paddingBottom} ${totalPoints} ${chartWidth},${chartHeight - paddingBottom}`;
                return (
                  <>
                    <polygon points={areaPoints} fill="url(#totalGradient)" />
                    <polyline
                      points={totalPoints}
                      fill="none"
                      stroke="#204ECF"
                      strokeWidth="1.2"
                      strokeOpacity="1"
                      vectorEffect="non-scaling-stroke"
                    />
                    {/* Invisible hover circles for each data point */}
                    {total.map((value, i) => {
                      const x = (i / (total.length - 1 || 1)) * chartWidth;
                      const y = valueToY(value);
                      return (
                        <circle
                          key={i}
                          cx={x}
                          cy={y}
                          r="3"
                          fill="transparent"
                          stroke="none"
                          style={{ cursor: 'crosshair' }}
                        />
                      );
                    })}
                  </>
                );
              })()}
              </svg>
            </div>
            <div className="chart-x-axis-wrapper">
              <div className="chart-x-axis-spacer"></div>
              <div className="chart-x-axis">
                {xAxisLabels.map((item, i) => {
                  const leftPercent = dates.length > 1 ? (item.index / (dates.length - 1)) * 100 : 0;
                  return (
                    <span key={i} style={{ left: `${leftPercent}%` }}>
                      {item.label}
                    </span>
                  );
                })}
              </div>
            </div>
            {/* Tooltip */}
            {hoveredPoint && (
              <div 
                className="chart-tooltip"
                style={{
                  left: `${tooltipPosition.x}px`,
                  top: `${tooltipPosition.y - 10}px`,
                  transform: 'translate(-50%, -100%)',
                }}
              >
                <div className="tooltip-date">{formatDateTime(hoveredPoint.date)}</div>
                <div className="tooltip-balance">{formatCurrency(hoveredPoint.balance)}</div>
              </div>
            )}
          </div>
          {/* Legend */}
          <div className="balance-history-legend">
            <div className="legend-item-total">
              <span className="legend-line" style={{ backgroundColor: '#204ECF', width: '16px', height: '2px' }} />
              <span className="legend-label">Total Portfolio</span>
            </div>
            {exchanges.slice(0, 5).map(exchange => (
              <div key={exchange.name} className="legend-item">
                <span className="legend-line" style={{ backgroundColor: exchange.color, width: '12px', height: '1px' }} />
                <span className="legend-label">{exchange.name}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Exchange List Item
function ExchangeListItem({ name, color, total, assets, onClick }) {
  return (
    <div className={`exchange-list-item ${onClick ? 'clickable' : ''}`} onClick={onClick}>
      <div className="exchange-header">
        <div className="exchange-name">
          <span className="exchange-dot" style={{ backgroundColor: color }} />
          <span>{name}</span>
        </div>
        <span className="exchange-total mono">{formatCurrency(total)}</span>
      </div>
      <div className="exchange-assets">
        {assets.map((asset, i) => (
          <span key={i} className="asset-mini">
            {asset.symbol}
            {asset.accountType && (
              <span className="account-type-badge" title={`Account type: ${asset.accountType}`}>
                {asset.accountType}
              </span>
            )}
            : {formatToken(asset.amount, true)}
          </span>
        ))}
      </div>
    </div>
  );
}

// Explorer URLs by chain
const EXPLORER_URLS = {
  arbitrum: 'https://arbiscan.io',
  ethereum: 'https://etherscan.io',
};

// Get explorer URL for tx
const getExplorerUrl = (txHash, chain = 'arbitrum') => {
  if (!txHash) return null;
  const baseUrl = EXPLORER_URLS[chain] || EXPLORER_URLS.arbitrum;
  return `${baseUrl}/tx/${txHash}`;
};

// Transaction History Tile Component
function TransactionHistoryTile({ selectedItem, onClose }) {
  const { activeTimezone } = useTimezone();
  const [activeTab, setActiveTab] = useState('deposits');
  const [copiedAddress, setCopiedAddress] = useState(null);

  const isExchange = selectedItem?.type === 'exchange';
  const isWallet = selectedItem?.type === 'wallet';

  // Fetch deposits for exchanges and wallets - always call hook, conditionally fetch
  const { data: depositsData, error: depositsError } = useSWR(
    selectedItem?.id && isExchange
      ? `/deposits/history?exchange=${selectedItem.id}&limit=5`
      : selectedItem?.data?.address && isWallet
      ? `/deposits/history?wallet=${selectedItem.data.address}&limit=5`
      : null,
    fetcher,
    { refreshInterval: 60000 }
  );

  // Fetch withdrawals for exchanges and wallets - always call hook, conditionally fetch
  const { data: withdrawalsData, error: withdrawalsError } = useSWR(
    selectedItem?.id && isExchange
      ? `/withdrawals/history?exchange=${selectedItem.id}&limit=5`
      : selectedItem?.data?.address && isWallet
      ? `/withdrawals/history?wallet=${selectedItem.data.address}&limit=5`
      : null,
    fetcher,
    { refreshInterval: 60000 }
  );

  // Process deposits for both exchanges and wallets - always call useMemo
  const processedDeposits = useMemo(() => {
    if (!depositsData?.deposits) return [];
    return depositsData.deposits.slice(0, 5).map(deposit => ({
      asset: deposit.asset || 'N/A',
      amount: deposit.amount || 0,
      timestamp: deposit.timestamp ? new Date(deposit.timestamp) : new Date(),
      txHash: deposit.txHash || '',
      status: deposit.status || 'pending',
      fromAddress: deposit.address || '',
      chain: isWallet ? (deposit.chain || 'arbitrum') : 'arbitrum', // Use chain from wallet data if available
    }));
  }, [depositsData, isWallet]);

  // Process withdrawals for both exchanges and wallets - always call useMemo
  const processedWithdrawals = useMemo(() => {
    if (!withdrawalsData?.withdrawals) return [];
    return withdrawalsData.withdrawals.slice(0, 5).map(withdrawal => ({
      asset: withdrawal.asset || 'N/A',
      amount: withdrawal.amount || 0,
      timestamp: withdrawal.startedAt ? new Date(withdrawal.startedAt) : new Date(),
      txHash: withdrawal.txHash || withdrawal.withdrawalId || '',
      status: withdrawal.status || 'pending',
      toAddress: withdrawal.toAddress || '',
      fee: withdrawal.fee || 0,
      chain: isWallet ? (withdrawal.chain || 'arbitrum') : 'arbitrum', // Use chain from wallet data if available
    }));
  }, [withdrawalsData, isWallet]);

  // Format full date and time - must be defined after useTimezone hook
  const formatFullDateTime = useCallback((timestamp) => {
    if (!activeTimezone) return 'N/A';
    return formatTimestampWithTimezone(timestamp, {
      timezone: activeTimezone,
      includeDate: true,
      includeTime: true,
      format: 'full',
    }) || 'N/A';
  }, [activeTimezone]);

  // Format transaction amount
  const formatTransactionAmount = (amount, asset) => {
    if (!amount) return 'N/A';
    const num = parseFloat(amount);
    if (isNaN(num)) return 'N/A';
    return `${num.toLocaleString(undefined, { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 6 
    })} ${asset || ''}`;
  };

  // Copy to clipboard
  const handleCopyAddress = async (address) => {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(address);
      setTimeout(() => setCopiedAddress(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Handle manual refresh
  const handleRefresh = async (e) => {
    e.stopPropagation();
    const depositsKey = isExchange && selectedItem?.id
      ? `/deposits/history?exchange=${selectedItem.id}&limit=5`
      : isWallet && selectedItem?.data?.address
      ? `/deposits/history?wallet=${selectedItem.data.address}&limit=5`
      : null;
    
    const withdrawalsKey = isExchange && selectedItem?.id
      ? `/withdrawals/history?exchange=${selectedItem.id}&limit=5`
      : isWallet && selectedItem?.data?.address
      ? `/withdrawals/history?wallet=${selectedItem.data.address}&limit=5`
      : null;

    if (depositsKey) {
      await mutate(depositsKey);
    }
    if (withdrawalsKey) {
      await mutate(withdrawalsKey);
    }
  };

  // Early return after all hooks
  if (!selectedItem) {
    return (
      <div className="chart-card">
        <h3>Click on Exchange or Wallet</h3>
        <div className="empty-chart-hint">
          <span>Click on an exchange or wallet above to see transaction history</span>
        </div>
      </div>
    );
  }

  // Compute final data - after all hooks
  const deposits = processedDeposits;
  const withdrawals = processedWithdrawals;

  const isLoading = (!depositsData && !depositsError) || (!withdrawalsData && !withdrawalsError);
  const hasError = depositsError || withdrawalsError;

  // Log errors and data for debugging
  if (depositsError) {
    console.error('[TransactionHistory] Deposits error:', depositsError);
  }
  if (withdrawalsError) {
    console.error('[TransactionHistory] Withdrawals error:', withdrawalsError);
  }
  // Debug logging for withdrawals and deposits
  if (selectedItem?.id || selectedItem?.data?.address) {
    console.log('[TransactionHistory] Debug:', {
      type: isExchange ? 'exchange' : 'wallet',
      id: isExchange ? selectedItem.id : selectedItem.data.address,
      hasDepositsData: !!depositsData,
      depositsCount: depositsData?.deposits?.length || 0,
      hasWithdrawalsData: !!withdrawalsData,
      withdrawalsCount: withdrawalsData?.withdrawals?.length || 0,
      processedDepositsCount: processedDeposits.length,
      processedWithdrawalsCount: processedWithdrawals.length,
    });
  }

  return (
    <div className="chart-card">
      <h3>
        {isExchange ? `${selectedItem.data.name} Transactions` : `${selectedItem.data.name || 'Wallet'} Transactions`}
        <div className="chart-header-actions">
          <button 
            className="refresh-chart-btn" 
            onClick={handleRefresh}
            title="Refresh data"
          >
            <RefreshCw size={16} />
          </button>
          <button 
            className="close-chart-btn" 
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            title="Close"
          >
            ×
          </button>
        </div>
      </h3>
      <div className="transaction-tabs">
        <button
          className={`transaction-tab ${activeTab === 'deposits' ? 'active' : ''}`}
          onClick={() => setActiveTab('deposits')}
        >
          Deposits ({deposits.length})
        </button>
        <button
          className={`transaction-tab ${activeTab === 'withdrawals' ? 'active' : ''}`}
          onClick={() => setActiveTab('withdrawals')}
        >
          Withdrawals ({withdrawals.length})
        </button>
      </div>
      <div className="transaction-history-container">
        {hasError ? (
          <div className="transaction-error">
            <span>Failed to load transactions.</span>
            <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#888' }}>
              {depositsError && <div>Deposits: {depositsError.message || depositsError.toString()}</div>}
              {withdrawalsError && <div>Withdrawals: {withdrawalsError.message || withdrawalsError.toString()}</div>}
            </div>
          </div>
        ) : isLoading ? (
          <div className="transaction-loading">
            <div className="loading-spinner-small"></div>
            <span>Loading transactions...</span>
          </div>
        ) : activeTab === 'deposits' ? (
          deposits.length > 0 ? (
            <div className="transaction-list">
              {deposits.map((tx, idx) => {
                const explorerUrl = getExplorerUrl(tx.txHash, tx.chain);
                return (
                  <div key={idx} className="transaction-item">
                    <div className="transaction-main">
                      <div className="transaction-asset">{tx.asset || 'N/A'}</div>
                      <div className="transaction-amount mono">
                        {formatTransactionAmount(tx.amount, tx.asset)}
                      </div>
                    </div>
                    {tx.fromAddress && (
                      <div className="transaction-destination">
                        <span className="transaction-destination-label">From:</span>
                        <span className="transaction-destination-address mono" title={tx.fromAddress}>
                          {shortenAddress(tx.fromAddress, 6, 4)}
                        </span>
                        <button
                          className="transaction-copy-btn"
                          onClick={() => handleCopyAddress(tx.fromAddress)}
                          title="Copy address"
                        >
                          {copiedAddress === tx.fromAddress ? (
                            <Check size={12} />
                          ) : (
                            <Copy size={12} />
                          )}
                        </button>
                      </div>
                    )}
                    <div className="transaction-meta">
                      <span className="transaction-time">{formatFullDateTime(tx.timestamp)}</span>
                      {tx.txHash && explorerUrl && (
                        <a
                          href={explorerUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="transaction-explorer-link"
                          title="View on blockchain explorer"
                        >
                          <ExternalLink size={12} />
                          {shortenAddress(tx.txHash, 6, 4)}
                        </a>
                      )}
                      {tx.status && (
                        <span className={`transaction-status ${tx.status.toLowerCase()}`}>
                          {tx.status}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="transaction-empty">No recent deposits</div>
          )
        ) : (
          withdrawals.length > 0 ? (
            <div className="transaction-list">
              {withdrawals.map((tx, idx) => {
                const explorerUrl = getExplorerUrl(tx.txHash, tx.chain);
                return (
                  <div key={idx} className="transaction-item">
                    <div className="transaction-main">
                      <div className="transaction-asset">{tx.asset || 'N/A'}</div>
                      <div className="transaction-amount mono">
                        {formatTransactionAmount(tx.amount, tx.asset)}
                        {tx.fee && parseFloat(tx.fee) > 0 && (
                          <span className="transaction-fee"> (fee: {formatToken(tx.fee)})</span>
                        )}
                      </div>
                    </div>
                    {tx.toAddress && (
                      <div className="transaction-destination">
                        <span className="transaction-destination-label">To:</span>
                        <span className="transaction-destination-address mono" title={tx.toAddress}>
                          {shortenAddress(tx.toAddress, 6, 4)}
                        </span>
                        <button
                          className="transaction-copy-btn"
                          onClick={() => handleCopyAddress(tx.toAddress)}
                          title="Copy address"
                        >
                          {copiedAddress === tx.toAddress ? (
                            <Check size={12} />
                          ) : (
                            <Copy size={12} />
                          )}
                        </button>
                      </div>
                    )}
                    <div className="transaction-meta">
                      <span className="transaction-time">{formatFullDateTime(tx.timestamp)}</span>
                      {tx.txHash && explorerUrl && (
                        <a
                          href={explorerUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="transaction-explorer-link"
                          title="View on blockchain explorer"
                        >
                          <ExternalLink size={12} />
                          {shortenAddress(tx.txHash, 6, 4)}
                        </a>
                      )}
                      {tx.status && (
                        <span className={`transaction-status ${tx.status.toLowerCase()}`}>
                          {tx.status}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="transaction-empty">No recent withdrawals</div>
          )
        )}
      </div>
    </div>
  );
}

// Consolidated List Tile with Tabs
function ConsolidatedListTile({ exchanges, wallets, exchangesTotal, treasuryTotal, onSelectItem }) {
  const [activeTab, setActiveTab] = useState('exchanges');

  return (
    <div className="list-card">
      <div className="list-header">
        <div className="list-tabs">
          <button
            className={`list-tab ${activeTab === 'exchanges' ? 'active' : ''}`}
            onClick={() => setActiveTab('exchanges')}
          >
            Exchanges
            <span className="list-tab-total mono">{formatCurrency(exchangesTotal)}</span>
          </button>
          <button
            className={`list-tab ${activeTab === 'wallets' ? 'active' : ''}`}
            onClick={() => setActiveTab('wallets')}
          >
            Wallets
            <span className="list-tab-total mono">{formatCurrency(treasuryTotal)}</span>
          </button>
        </div>
      </div>
      <div className="list-content">
        {activeTab === 'exchanges' ? (
          <>
            {exchanges.map((exchange) => (
              <ExchangeListItem
                key={exchange.id}
                name={exchange.name}
                color={exchange.color}
                total={exchange.total}
                assets={exchange.assets}
                onClick={() => onSelectItem({ type: 'exchange', id: exchange.id, data: exchange })}
              />
            ))}
            {exchanges.length === 0 && (
              <div className="empty-list">No exchange balances</div>
            )}
          </>
        ) : (
          <>
            {wallets.map((wallet) => (
              <TreasuryChainItem
                key={wallet.address || wallet.name}
                chain={wallet.name}
                chains={wallet.chains || []}
                total={wallet.total}
                assets={wallet.assets}
                address={wallet.address}
                isMain={wallet.isMain === true}
                isIntermediary={wallet.isIntermediary === true}
                onClick={() => onSelectItem({ type: 'wallet', id: wallet.address || wallet.name, data: wallet })}
              />
            ))}
            {wallets.length === 0 && (
              <div className="empty-list">No wallet balances</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Treasury Chain Item
function TreasuryChainItem({ chain, chains, total, assets, address, isMain, isIntermediary, onClick }) {
  const shortenAddress = (addr, start = 6, end = 4) => {
    if (!addr || addr.length < start + end + 3) return addr;
    return `${addr.slice(0, start)}...${addr.slice(-end)}`;
  };
  
  // Chain info mapping
  const chainInfoMap = {
    arbitrum: { short: 'ARB', color: '#28a0f0' },
    ethereum: { short: 'ETH', color: '#627eea' },
    polygon: { short: 'MATIC', color: '#8247e5' },
    base: { short: 'BASE', color: '#0052ff' },
    optimism: { short: 'OP', color: '#ff0420' },
    avalanche: { short: 'AVAX', color: '#e84142' },
    bsc: { short: 'BSC', color: '#f3ba2f' },
    solana: { short: 'SOL', color: '#14f195' },
    bitcoin: { short: 'BTC', color: '#f7931a' },
  };
  
  return (
    <div className={`treasury-chain-item ${onClick ? 'clickable' : ''}`} onClick={onClick}>
      <div className="chain-header">
        <div className="chain-name">
          {/* Show chain tags for all chains */}
          {chains && chains.length > 0 ? (
            <div className="chain-tags-inline">
              {chains.map((chainId) => {
                const chainInfo = chainInfoMap[chainId] || { short: chainId.toUpperCase(), color: '#666' };
                return (
                  <span key={chainId} className={`chain-tag-small ${chainId}`}>
                    {chainInfo.short}
                  </span>
                );
              })}
            </div>
          ) : null}
          <span>{chain}</span>
          {isIntermediary && <span className="intermediary-wallet-badge">PROXY</span>}
          {address && (
            <span className="address-tag-small" title={address}>
              {shortenAddress(address)}
            </span>
          )}
        </div>
        <span className="chain-total mono">{formatCurrency(total)}</span>
      </div>
      <div className="chain-assets">
        {assets.map((asset, i) => {
          // Show chain tag for each asset if chain info is available
          const chainTag = asset.chain ? (chainInfoMap[asset.chain]?.short || asset.chain.toUpperCase()) : null;
          return (
            <span key={i} className="asset-mini">
              {asset.symbol}
              {chainTag && <span className="asset-chain-tag">{chainTag}</span>}
              : {formatToken(asset.amount, true)}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// Account Type Item Component - Displays balance for one account type (receives data as prop)
function AccountTypeItem({ accountTypeLabel, balances, isExpanded, onToggle, accountType }) {
  let total = 0;
  Object.values(balances).forEach(balanceData => {
    const equity = parseFloat(balanceData.equity) || parseFloat(balanceData.available) || 0;
    total += equity;
  });
  
  // Always show unified accounts, even with $0 balance
  const isUnified = accountType === 'unified';
  const hasData = Object.keys(balances).length > 0 && total > 0;
  const shouldShow = hasData || isUnified;
  
  return (
    <div className="account-type-group">
      <div 
        className={`account-type-header ${isUnified ? 'unified-account' : ''}`}
        onClick={() => shouldShow && onToggle()}
        style={{ cursor: shouldShow ? 'pointer' : 'default', opacity: shouldShow ? 1 : 0.5 }}
      >
        <div className="account-type-name-row">
          {shouldShow && (
            <ChevronDown 
              size={14} 
              style={{ 
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }} 
            />
          )}
          <span className="account-type-label">
            {accountTypeLabel}
          </span>
        </div>
        <span className="account-type-total mono">
          {formatCurrency(total)}
        </span>
      </div>
      {isExpanded && shouldShow && (
        <div className="account-type-assets">
          {Object.entries(balances).map(([asset, balanceData]) => {
            const equity = parseFloat(balanceData.equity) || parseFloat(balanceData.available) || 0;
            if (equity === 0 && !isUnified) return null;
            return (
              <div key={asset} className="account-type-asset-item">
                <span className="asset-label">{asset}</span>
                <span className="asset-value mono">{formatCurrency(equity)}</span>
              </div>
            );
          })}
          {isUnified && Object.keys(balances).length === 0 && (
            <div className="account-type-asset-item">
              <span className="asset-label" style={{ color: '#888', fontStyle: 'italic' }}>No balances</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Exchange Breakdown Tile - Shows account type breakdowns with double donut chart
function ExchangeBreakdownTile({ exchange, exchangeId }) {
  // Stabilize the account types array to prevent hook count changes
  const supportedAccountTypes = useMemo(
    () => EXCHANGE_ACCOUNT_TYPES[exchangeId] || [],
    [exchangeId]
  );
  const [expandedAccountTypes, setExpandedAccountTypes] = useState(new Set());
  
  const accountTypeLabels = useMemo(() => ({
    spot: 'Spot',
    perp: 'Perp',
    futures: 'Futures',
    margin: 'Margin',
    funding: 'Funding',
    main: 'Main (Funding)',
    trade: 'Trade (Spot)',
    unified: 'Unified',
  }), []);
  
  // Fetch balances for each account type - call hooks individually at top level
  const spotData = useSWR(
    supportedAccountTypes.includes('spot') ? `/balances?accountType=spot` : null,
    fetcher,
    { refreshInterval: 0, revalidateOnFocus: false }
  );
  const perpData = useSWR(
    supportedAccountTypes.includes('perp') ? `/balances?accountType=perp` : null,
    fetcher,
    { refreshInterval: 0, revalidateOnFocus: false }
  );
  const futuresData = useSWR(
    supportedAccountTypes.includes('futures') ? `/balances?accountType=futures` : null,
    fetcher,
    { refreshInterval: 0, revalidateOnFocus: false }
  );
  const marginData = useSWR(
    supportedAccountTypes.includes('margin') ? `/balances?accountType=margin` : null,
    fetcher,
    { refreshInterval: 0, revalidateOnFocus: false }
  );
  const fundingData = useSWR(
    supportedAccountTypes.includes('funding') ? `/balances?accountType=funding` : null,
    fetcher,
    { refreshInterval: 0, revalidateOnFocus: false }
  );
  const mainData = useSWR(
    supportedAccountTypes.includes('main') ? `/balances?accountType=main` : null,
    fetcher,
    { refreshInterval: 0, revalidateOnFocus: false }
  );
  const tradeData = useSWR(
    supportedAccountTypes.includes('trade') ? `/balances?accountType=trade` : null,
    fetcher,
    { refreshInterval: 0, revalidateOnFocus: false }
  );
  const unifiedData = useSWR(
    supportedAccountTypes.includes('unified') ? `/balances?accountType=unified` : null,
    fetcher,
    { refreshInterval: 0, revalidateOnFocus: false }
  );
  
  // Build accountTypeBalances object from fetched data - use useMemo to make it reactive
  const accountTypeBalances = useMemo(() => {
    const balances = {};
    if (supportedAccountTypes.includes('spot') && spotData.data?.exchanges?.[exchangeId]) {
      balances.spot = spotData.data.exchanges[exchangeId];
    }
    if (supportedAccountTypes.includes('perp') && perpData.data?.exchanges?.[exchangeId]) {
      balances.perp = perpData.data.exchanges[exchangeId];
    }
    if (supportedAccountTypes.includes('futures') && futuresData.data?.exchanges?.[exchangeId]) {
      balances.futures = futuresData.data.exchanges[exchangeId];
    }
    if (supportedAccountTypes.includes('margin') && marginData.data?.exchanges?.[exchangeId]) {
      balances.margin = marginData.data.exchanges[exchangeId];
    }
    if (supportedAccountTypes.includes('funding') && fundingData.data?.exchanges?.[exchangeId]) {
      balances.funding = fundingData.data.exchanges[exchangeId];
    }
    if (supportedAccountTypes.includes('main') && mainData.data?.exchanges?.[exchangeId]) {
      balances.main = mainData.data.exchanges[exchangeId];
    }
    if (supportedAccountTypes.includes('trade') && tradeData.data?.exchanges?.[exchangeId]) {
      balances.trade = tradeData.data.exchanges[exchangeId];
    }
    if (supportedAccountTypes.includes('unified') && unifiedData.data?.exchanges?.[exchangeId]) {
      balances.unified = unifiedData.data.exchanges[exchangeId];
    }
    return balances;
  }, [supportedAccountTypes, exchangeId, spotData.data, perpData.data, futuresData.data, marginData.data, fundingData.data, mainData.data, tradeData.data, unifiedData.data]);
  
  // Calculate asset data for double donut chart: { USDT: { total, byAccountType }, ... }
  // Use account type balances to ensure accuracy - aggregate by asset across all account types
  // Also merge with exchange.assets to ensure we have all assets (including those not in account types)
  const assetData = useMemo(() => {
    const focusAssets = ['USDT', 'USDC', 'ETH'];
    const data = {};
    
    // First, aggregate balances from all account types by asset
    Object.entries(accountTypeBalances).forEach(([accountType, balances]) => {
      Object.entries(balances).forEach(([asset, balanceData]) => {
        const assetKey = focusAssets.includes(asset) ? asset : 'Other';
        if (!data[assetKey]) {
          data[assetKey] = { total: 0, byAccountType: {} };
        }
        // Always use usdValue if available (backend calculates this for all assets)
        // Fallback to equity/available only for stablecoins (USDT, USDC) where 1:1 USD
        const usdValue = parseFloat(balanceData.usdValue) || 
          ((asset === 'USDT' || asset === 'USDC') ? (parseFloat(balanceData.equity) || parseFloat(balanceData.available) || 0) : 0);
        if (usdValue > 0) {
          // Add to total
          data[assetKey].total += usdValue;
          // Add to account type breakdown
          if (!data[assetKey].byAccountType[accountType]) {
            data[assetKey].byAccountType[accountType] = 0;
          }
          data[assetKey].byAccountType[accountType] += usdValue;
        }
      });
    });
    
    // Also merge with exchange.assets to ensure we capture all assets from the main balances endpoint
    // This ensures "Other" category and all assets are included, matching the left panel
    if (exchange.assets && Array.isArray(exchange.assets)) {
      exchange.assets.forEach(asset => {
        const assetKey = focusAssets.includes(asset.symbol) ? asset.symbol : 'Other';
        if (!data[assetKey]) {
          data[assetKey] = { total: 0, byAccountType: {} };
        }
        const value = asset.usdValue || parseFloat(asset.amount) || 0;
        // Use the maximum of account-type aggregated value or exchange.assets value
        // This ensures we don't double-count, but also don't miss assets
        if (value > data[assetKey].total) {
          data[assetKey].total = value;
        }
      });
    }
    
    // Ensure all focus assets exist (even if 0)
    focusAssets.forEach(asset => {
      if (!data[asset]) {
        data[asset] = { total: 0, byAccountType: {} };
      }
    });
    if (!data.Other) {
      data.Other = { total: 0, byAccountType: {} };
    }
    
    return data;
  }, [exchange.assets, accountTypeBalances]);
  
  // Calculate totals per account type for display
  // Prefer exchange-provided totals when available
  const accountTypeTotals = useMemo(() => {
    const totals = {};
    Object.entries(accountTypeBalances).forEach(([accountType, balances]) => {
      // Try to get exchange-provided total from the API response
      let exchangeProvidedTotal = null;
      
      // Check each account type's data response for exchange-provided total
      const accountTypeDataMap = {
        spot: spotData,
        perp: perpData,
        futures: futuresData,
        margin: marginData,
        funding: fundingData,
        main: mainData,
        trade: tradeData,
        unified: unifiedData,
      };
      
      const accountTypeData = accountTypeDataMap[accountType];
      if (accountTypeData?.data?.exchangeTotals?.[exchangeId]) {
        exchangeProvidedTotal = parseFloat(accountTypeData.data.exchangeTotals[exchangeId]);
      }
      
      if (exchangeProvidedTotal !== null && exchangeProvidedTotal > 0) {
        // Use exchange-provided total
        totals[accountType] = exchangeProvidedTotal;
      } else {
        // Compute from balances
        let total = 0;
        Object.entries(balances).forEach(([asset, balanceData]) => {
          // Always use usdValue if available (backend calculates this for all assets)
          // Fallback to equity/available only for stablecoins (USDT, USDC) where 1:1 USD
          const usdValue = parseFloat(balanceData.usdValue) || 
            ((asset === 'USDT' || asset === 'USDC') ? (parseFloat(balanceData.equity) || parseFloat(balanceData.available) || 0) : 0);
          
          // Debug log if usdValue is 0 but we have a balance
          if (usdValue === 0 && (parseFloat(balanceData.equity) || parseFloat(balanceData.available) || 0) > 0 && asset !== 'USDT' && asset !== 'USDC') {
            console.warn(`Account type ${accountType} (${exchangeId}): ${asset} has balance but usdValue is 0`, {
              asset,
              accountType,
              exchangeId,
              balanceData,
              equity: balanceData.equity,
              available: balanceData.available,
              usdValue: balanceData.usdValue,
            });
          }
          
          total += usdValue;
        });
        totals[accountType] = total;
      }
    });
    return totals;
  }, [accountTypeBalances, spotData, perpData, futuresData, marginData, fundingData, mainData, tradeData, unifiedData, exchangeId]);
  
  const toggleAccountType = useCallback((accountType) => {
    setExpandedAccountTypes(prev => {
      const next = new Set(prev);
      if (next.has(accountType)) {
        next.delete(accountType);
      } else {
        next.add(accountType);
      }
      return next;
    });
  }, []);
  
  // Group assets for display: USDT, USDC, ETH, Other
  // Use exchange.assets as the source of truth to match the left panel exactly
  // Always include "Other" even if 0
  const groupedAssets = useMemo(() => {
    const focusAssets = ['USDT', 'USDC', 'ETH'];
    const grouped: Record<string, number> = { USDT: 0, USDC: 0, ETH: 0, Other: 0 };
    
    // Use exchange.assets as primary source (matches left panel)
    if (exchange.assets && Array.isArray(exchange.assets)) {
      exchange.assets.forEach(asset => {
        const assetKey = focusAssets.includes(asset.symbol) ? asset.symbol : 'Other';
        const value = asset.usdValue || parseFloat(asset.amount) || 0;
        grouped[assetKey] += value;
      });
    } else {
      // Fallback to assetData if exchange.assets is not available
      Object.entries(assetData).forEach(([symbol, data]) => {
        const assetKey = focusAssets.includes(symbol) ? symbol : 'Other';
        grouped[assetKey] += data.total || 0;
      });
    }
    
    // Always return all four categories, even if some are 0
    return Object.entries(grouped)
      .map(([symbol, value]) => ({ symbol, value }))
      .sort((a, b) => {
        // Sort: USDT, USDC, ETH, then Others
        const order = { USDT: 0, USDC: 1, ETH: 2, Other: 3 };
        return (order[a.symbol] ?? 99) - (order[b.symbol] ?? 99);
      });
  }, [assetData, exchange.assets]);
  
  // Calculate total balance
  const totalBalance = exchange.total || groupedAssets.reduce((sum, asset) => sum + asset.value, 0);
  
  // Prepare data for top pie chart: USDT, USDC, ETH, Others
  // Filter out zero values for the chart (but keep in the legend)
  const topChartData = groupedAssets
    .filter(asset => asset.value > 0)
    .map(asset => ({
      name: asset.symbol,
      value: asset.value
    }));
  
  return (
    <div className="exchange-breakdown">
      {/* Top: Pie Chart with Total Balance by Assets */}
      <div className="donut-container">
        <DonutChart 
          data={topChartData}
          colors={ASSET_COLORS} 
          size={200}
          title={exchange.name}
          showTimestamp={false}
        />
      </div>
      
      {/* Middle: Account Type Breakdown - Only show if exchange supports account types */}
      {supportedAccountTypes.length > 0 && (() => {
        // Sort account types to prioritize unified accounts
        const sortedAccountTypes = [...supportedAccountTypes].sort((a, b) => {
          // Unified accounts first
          if (a === 'unified' && b !== 'unified') return -1;
          if (a !== 'unified' && b === 'unified') return 1;
          // Then by balance (higher first)
          const aTotal = accountTypeTotals[a] || 0;
          const bTotal = accountTypeTotals[b] || 0;
          if (aTotal !== bTotal) return bTotal - aTotal;
          // Finally alphabetical
          return a.localeCompare(b);
        });
        
        return (
          <div className="account-type-breakdown">
            <div className="account-type-breakdown-header">
              <h4>By Account Type</h4>
            </div>
            {sortedAccountTypes.map(accountType => {
              const balances = accountTypeBalances[accountType] || {};
              const total = accountTypeTotals[accountType] || 0;
              // Show account types if they have balances OR if data is still loading (to prevent flickering)
              const isLoading = (accountType === 'spot' && spotData.isLoading) ||
                               (accountType === 'funding' && fundingData.isLoading) ||
                               (accountType === 'perp' && perpData.isLoading) ||
                               (accountType === 'futures' && futuresData.isLoading) ||
                               (accountType === 'margin' && marginData.isLoading) ||
                               (accountType === 'main' && mainData.isLoading) ||
                               (accountType === 'trade' && tradeData.isLoading) ||
                               (accountType === 'unified' && unifiedData.isLoading);
              // Always show unified accounts, even if balance is 0
              // Show other account types if they have a balance OR if data is loading
              const shouldShow = total > 0 || accountType === 'unified' || isLoading;
              
              if (!shouldShow) return null;
              
              // Prepare asset list for this account type
              const accountTypeAssets = [];
              const focusAssets = ['USDT', 'USDC', 'ETH'];
              Object.entries(balances).forEach(([asset, balanceData]) => {
                // Always use usdValue if available (backend calculates this for all assets)
                // Fallback to equity/available only for stablecoins (USDT, USDC) where 1:1 USD
                const usdValue = parseFloat(balanceData.usdValue) || 
                  ((asset === 'USDT' || asset === 'USDC') ? (parseFloat(balanceData.equity) || parseFloat(balanceData.available) || 0) : 0);
                if (usdValue > 0) {
                  const assetKey = focusAssets.includes(asset) ? asset : 'Other';
                  const existing = accountTypeAssets.find(item => item.symbol === assetKey);
                  if (existing) {
                    existing.value += usdValue;
                  } else {
                    accountTypeAssets.push({ symbol: assetKey, value: usdValue });
                  }
                }
              });
              
              // Sort assets: USDT, USDC, ETH, Other
              accountTypeAssets.sort((a, b) => {
                const order = { USDT: 0, USDC: 1, ETH: 2, Other: 3 };
                return (order[a.symbol] ?? 99) - (order[b.symbol] ?? 99);
              });
              
              return (
                <div key={accountType} className="account-type-group">
                  <div 
                    className={`account-type-header ${accountType === 'unified' ? 'unified-account' : ''}`}
                    onClick={() => toggleAccountType(accountType)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="account-type-name-row">
                      <ChevronDown 
                        size={14} 
                        style={{ 
                          transform: expandedAccountTypes.has(accountType) ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s'
                        }} 
                      />
                      <span className="account-type-label">
                        {accountTypeLabels[accountType] || accountType}
                      </span>
                    </div>
                    <span className="account-type-total mono">
                      {formatCurrency(total)}
                    </span>
                  </div>
                  {expandedAccountTypes.has(accountType) && (
                    <div className="account-type-details">
                      {accountTypeAssets.length > 0 ? (
                        <div className="account-type-assets">
                          {accountTypeAssets.map((asset, idx) => (
                            <div key={idx} className="account-type-asset-item">
                              <span className="asset-label">{asset.symbol}</span>
                              <span className="asset-value mono">{formatCurrency(asset.value)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="account-type-empty">
                          <span style={{ color: '#888', fontStyle: 'italic' }}>No balances</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}
      
      {/* Bottom: Total Assets Breakdown - Grouped as USDT, USDC, ETH, Other */}
      <div className="chart-legend">
        <div className="legend-section-header">Total Assets</div>
        {groupedAssets.map((asset, i) => (
          <div key={i} className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: ASSET_COLORS[asset.symbol] || '#666' }} />
            <span className="legend-label">{asset.symbol}</span>
            <span className="legend-value mono">{formatCurrency(asset.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Main Balances Dashboard
const DAYS_MAP = { '1D': 1, '7D': 7, '30D': 30, '90D': 90 };

function BalancesDashboard() {
  const { balances, isLoading, isValidating, isError, refresh, cacheTimestamp, isStale } = useBalances(); // No automatic polling - only refresh on manual button click or webhook
  const { activeTimezone } = useTimezone();
  const [selectedItem, setSelectedItem] = useState(null); // { type: 'exchange' | 'wallet', id: string, data: {...} }
  const [timeRange, setTimeRange] = useState('7D');
  
  // Fetch active transfers count
  const { data: transfersData } = useSWR('/transfers/history?limit=10', fetcher, {
    refreshInterval: 30000, // Refresh every 30 seconds instead of 10
  });
  
  const activeTransfers = useMemo(() => {
    if (!transfersData?.transfers) return { count: 0, pending: 0 };
    const active = transfersData.transfers.filter(t => 
      t.status === 'PENDING' || t.status === 'IN_PROGRESS'
    );
    const pending = transfersData.transfers.filter(t => t.status === 'PENDING');
    return { count: active.length, pending: pending.length };
  }, [transfersData]);

  // Calculate totals from balances (only use real data)
  const stats = useMemo(() => {
    if (!balances) {
      return {
        total: 0,
        exchanges: [],
        treasury: [],
        treasuryTotal: 0,
        byLocation: [],
        byAsset: [],
        exchangesTotal: 0,
      };
    }
    
    const { treasury, exchanges } = balances;
    let total = 0;
    const exchangeList = [];
    const treasuryList = [];
    const assetTotals = {};
    
    // Process treasury - new format: { [asset_address]: { available, chain, address, name, isMain } }
    if (treasury) {
      // Group by wallet address
      const treasuryByWallet = {};
      
      Object.entries(treasury).forEach(([key, data]) => {
        // Handle both old format (asset -> data) and new format (asset_chain_address -> data)
        const available = parseFloat(data.available || data) || 0;
        // Use USD value if available, otherwise calculate from available amount
        const usdValue = parseFloat(data.usdValue || 0) || available; // Fallback to available if no usdValue
        const chain = data.chain || 'Arbitrum';
        const address = data.address || null;
        const name = data.name || null;
        // Handle isMain as boolean or string "true"/"false"
        const isMain = data.isMain === true || data.isMain === "true";
        // Handle isIntermediary as boolean or string "true"/"false"
        const isIntermediary = data.isIntermediary === true || data.isIntermediary === "true";
        
        // Extract asset, chain, and address from key
        // New format: "ASSET_CHAIN_ADDRESS" (e.g., "USDT_arbitrum_0x...")
        // Old format: "ASSET_ADDRESS" or just "ASSET"
        const parts = key.split('_');
        const asset = parts[0].toUpperCase();
        
        // Determine chain and address from key or data
        let extractedChain = chain;
        let walletAddress = address;
        
        if (parts.length >= 3) {
          // New format: ASSET_CHAIN_ADDRESS
          extractedChain = parts[1]; // chain from key
          walletAddress = address || parts.slice(2).join('_'); // address from key or data
        } else if (parts.length === 2) {
          // Old format: ASSET_ADDRESS (no chain in key)
          walletAddress = address || parts[1];
        }
        
        // Use address as key, or fallback to chain if no address
        const walletKey = walletAddress?.toLowerCase() || extractedChain;
        
        if (!treasuryByWallet[walletKey]) {
          treasuryByWallet[walletKey] = {
            name: name,
            address: walletAddress || '',
            isMain: isMain, // Set initial value
            isIntermediary: isIntermediary, // Set initial value
            total: 0,
            assets: [],
            chains: new Set(), // Track which chains this wallet has balances on
          };
        } else {
          // For existing wallet, preserve true values (if any entry has isMain=true, keep it true)
          // Handle both boolean and string values
          const entryIsMain = data.isMain === true || data.isMain === "true";
          if (entryIsMain) {
            treasuryByWallet[walletKey].isMain = true;
          }
          // Preserve isIntermediary if true
          const entryIsIntermediary = data.isIntermediary === true || data.isIntermediary === "true";
          if (entryIsIntermediary) {
            treasuryByWallet[walletKey].isIntermediary = true;
          }
          // Update name if not set yet
          if (!treasuryByWallet[walletKey].name && name) {
            treasuryByWallet[walletKey].name = name;
          }
        }
        
        // Use USD value for totals, not raw balance amounts
        treasuryByWallet[walletKey].total += usdValue;
        treasuryByWallet[walletKey].assets.push({ symbol: asset, amount: available, usdValue: usdValue, chain: extractedChain });
        treasuryByWallet[walletKey].chains.add(extractedChain);
        
        total += usdValue;
        assetTotals[asset] = (assetTotals[asset] || 0) + usdValue;
      });
      
      // Convert to list and sort: main wallet first, then by name, then by address
      Object.entries(treasuryByWallet).forEach(([key, wallet]) => {
        const displayName = wallet.name || (wallet.address ? `${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)}` : 'Unknown');
        // Determine icon based on chains (prefer ARB if both, otherwise use first chain)
        const chainsArray = Array.from(wallet.chains || []);
        const icon = chainsArray.includes('arbitrum') ? 'ARB' : (chainsArray.includes('ethereum') ? 'ETH' : 'ARB');
        
        treasuryList.push({
          name: displayName,
          address: wallet.address,
          isMain: wallet.isMain === true, // Ensure boolean
          isIntermediary: wallet.isIntermediary === true, // Ensure boolean
          chains: chainsArray, // Pass chains array
          total: wallet.total,
          assets: wallet.assets,
        });
      });
      
      // Sort: intermediary wallet first, then by name
      treasuryList.sort((a, b) => {
        if (a.isIntermediary && !b.isIntermediary) return -1;
        if (!a.isIntermediary && b.isIntermediary) return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
    }
    
    // Process exchanges
    if (exchanges) {
      Object.entries(exchanges).forEach(([exchange, assets]) => {
        let exchangeTotal = 0;
        let unrealizedPnL = 0;
        const assetList = [];
        const accountTypeGroups = {}; // Group assets by account type
        const focusAssets = ['USDT', 'USDC', 'ETH'];
        const groupedAssets = { USDT: 0, USDC: 0, ETH: 0, Other: 0 };
        
        Object.entries(assets).forEach(([asset, data]) => {
          // Use usdValue if available (backend calculates this), otherwise fallback to equity/available
          // For stablecoins (USDT, USDC), equity is already in USD (1:1)
          const usdValue = parseFloat(data.usdValue) || 
            ((asset === 'USDT' || asset === 'USDC') ? (parseFloat(data.equity) || parseFloat(data.available) || 0) : 0);
          const pnl = parseFloat(data.unrealizedPnl) || 0;
          const accountType = data.accountType || null; // Account type (spot, futures, margin, funding, etc.)
          
          // Check if asset has balance but usdValue is 0 (missing price)
          const hasBalance = parseFloat(data.equity || data.available || '0') > 0;
          const hasUsdValue = usdValue > 0;
          
          // Log warning if asset has balance but no USD value (missing price)
          if (hasBalance && !hasUsdValue && asset !== 'USDT' && asset !== 'USDC') {
            console.warn(`Asset ${asset} has balance but usdValue is 0 (missing price?)`, {
              asset,
              equity: data.equity,
              available: data.available,
              usdValue: data.usdValue,
              price: data.price,
              exchange
            });
          }
          
          exchangeTotal += usdValue;
          unrealizedPnL += pnl;
          assetList.push({ symbol: asset, amount: usdValue, unrealizedPnl: pnl, accountType });
          assetTotals[asset] = (assetTotals[asset] || 0) + usdValue;
          
          // Group assets for display: USDT, USDC, ETH, Other
          const assetKey = focusAssets.includes(asset) ? asset : 'Other';
          groupedAssets[assetKey] += usdValue;
          
          // Group by account type for breakdown display
          if (accountType) {
            if (!accountTypeGroups[accountType]) {
              accountTypeGroups[accountType] = { total: 0, assets: [] };
            }
            accountTypeGroups[accountType].total += usdValue;
            accountTypeGroups[accountType].assets.push({ symbol: asset, amount: usdValue, unrealizedPnl: pnl });
          }
        });
        
        // Create grouped asset list for display (USDT, USDC, ETH, Other)
        const displayAssets = [];
        focusAssets.forEach(asset => {
          if (groupedAssets[asset] > 0) {
            displayAssets.push({ symbol: asset, amount: groupedAssets[asset] });
          }
        });
        if (groupedAssets.Other > 0) {
          displayAssets.push({ symbol: 'Other', amount: groupedAssets.Other });
        }
        
        // Use exchange-provided total if available, otherwise use computed total
        const exchangeProvidedTotal = balances?.exchangeTotals?.[exchange];
        const finalExchangeTotal = exchangeProvidedTotal !== undefined ? exchangeProvidedTotal : exchangeTotal;
        
        exchangeList.push({
          name: exchange.charAt(0).toUpperCase() + exchange.slice(1),
          id: exchange,
          total: finalExchangeTotal, // Use exchange-provided total if available
          unrealizedPnL,
          assets: displayAssets, // Always use grouped assets (USDT, USDC, ETH, Other)
          accountTypeGroups: Object.keys(accountTypeGroups).length > 0 ? accountTypeGroups : null, // Only include if we have account type data
          color: EXCHANGE_COLORS[exchange] || '#666',
        });
        
        total += exchangeTotal;
      });
    }
    
    // Sort by total
    exchangeList.sort((a, b) => b.total - a.total);
    
    // Calculate exchange vs treasury totals
    const exchangesTotal = exchangeList.reduce((sum, e) => sum + e.total, 0);
    const treasuryTotal = treasuryList.reduce((sum, t) => sum + t.total, 0);
    
    // By Location data for donut chart - include all treasury wallets
    const byLocation = [
      ...exchangeList.map(e => ({ name: e.id, value: e.total })),
      ...treasuryList.map(w => ({ 
        name: w.name || w.address || 'treasury', 
        value: w.total 
      })),
    ];
    
    // By Asset data for donut chart
    // Group assets: USDT, USDC, ETH, Other (all other assets grouped)
    const focusAssets = ['USDT', 'USDC', 'ETH'];
    const groupedAssetTotals = { USDT: 0, USDC: 0, ETH: 0, Other: 0 };
    
    Object.entries(assetTotals).forEach(([name, value]) => {
      const assetKey = focusAssets.includes(name) ? name : 'Other';
      groupedAssetTotals[assetKey] += value;
    });
    
    // Convert to array and sort: USDT, USDC, ETH, Other
    // Always include "Other" even if 0, but filter out focus assets if 0
    const byAsset = Object.entries(groupedAssetTotals)
      .map(([name, value]) => ({ name, value }))
      .filter(asset => {
        // Always show "Other" even if 0 (to indicate it exists but might have missing prices)
        if (asset.name === 'Other') return true;
        // Only show focus assets if they have value > 0
        return asset.value > 0;
      })
      .sort((a, b) => {
        const order = { USDT: 0, USDC: 1, ETH: 2, Other: 3 };
        return (order[a.name] ?? 99) - (order[b.name] ?? 99);
      });
    
    return { 
      total, 
      exchanges: exchangeList, 
      treasury: treasuryList, 
      treasuryTotal,
      byLocation, 
      byAsset,
      exchangesTotal,
    };
  }, [balances]);
  
  // Fetch real P&L data from API
  const { data: dailyPnLData } = useSWR('/analytics/daily-pnl?days=7', fetcher, {
    refreshInterval: 60000, // Refresh every minute
  });
  
  // Transform API data for the chart
  const dailyPnL = useMemo(() => {
    if (!dailyPnLData || !Array.isArray(dailyPnLData) || dailyPnLData.length === 0) {
      return []; // Return empty array - no mock data
    }
    
    return dailyPnLData.map(item => {
      // Format the date label (e.g., "Jan 6")
      const date = new Date(item.date);
      const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return {
        label,
        value: item.pnl || 0,
      };
    });
  }, [dailyPnLData]);
  
  const todayPnL = dailyPnL.length > 0 ? dailyPnL[dailyPnL.length - 1]?.value : 0;
  const weekPnL = dailyPnL.reduce((sum, d) => sum + d.value, 0);
  
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    return formatTimestampWithTimezone(timestamp, {
      timezone: activeTimezone,
      includeDate: true,
      includeTime: true,
      format: 'relative',
    });
  };

  if (isLoading && !balances) {
    return (
      <div className="dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>Loading dashboard...</span>
        </div>
      </div>
    );
  }

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Invalidate cache and fetch fresh data
      await refresh();
      // Also trigger SWR refresh for other data
      await Promise.all([
        mutate('/analytics/daily-pnl?days=7'),
        mutate(`/analytics/balance-history?days=${DAYS_MAP[timeRange] || 7}`),
        mutate('/transfers/history?limit=10'),
      ]);
      // Timestamp will be updated automatically by onSuccess callback in useBalances
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  return (
    <div className="dashboard">
      {/* Header with Refresh Button */}
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <h1 className="dashboard-title">
            Dashboard
            {cacheTimestamp && (
              <>
                {' '}
                <span className={`balance-timestamp ${isStale ? 'stale' : ''} ${isValidating ? 'updating' : ''}`}>
                  <span className="timestamp-label">
                    {isValidating ? 'Updating...' : isStale ? 'Last updated' : 'Updated'}
                  </span>
                  {' '}
                  <span className="timestamp-value">{formatTimestamp(cacheTimestamp)}</span>
                </span>
              </>
            )}
          </h1>
        </div>
        <button 
          className={`refresh-button ${isRefreshing || isValidating ? 'refreshing' : ''}`}
          onClick={handleRefresh}
          disabled={isRefreshing || isValidating}
          title="Refresh balances"
        >
          <RefreshCw size={18} />
          <span className="refresh-text">Refresh</span>
        </button>
      </div>

      {/* Stats Row */}
      <div className="stats-row">
        {dailyPnL.length > 0 && (
          <StatsCard
            title="Today's P&L"
            value={formatCurrency(todayPnL)}
            subtitle={stats.total > 0 ? formatPercent((todayPnL / stats.total) * 100) : null}
            icon={todayPnL >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            trend={todayPnL >= 0 ? 'positive' : 'negative'}
          />
        )}
        {dailyPnL.length > 0 && (
          <StatsCard
            title="7-Day P&L"
            value={formatCurrency(weekPnL)}
            subtitle={stats.total > 0 ? formatPercent((weekPnL / stats.total) * 100) : null}
            icon={weekPnL >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            trend={weekPnL >= 0 ? 'positive' : 'negative'}
          />
        )}
        {activeTransfers.count > 0 && (
          <StatsCard
            title="Active Transfers"
            value={activeTransfers.count.toString()}
            subtitle={activeTransfers.pending > 0 ? `${activeTransfers.pending} pending approval` : null}
            trend={activeTransfers.pending > 0 ? 'warning' : null}
          />
        )}
      </div>

      {/* Charts Row - Overview Charts */}
      <div className="charts-row">
        {/* By Location */}
        <div className="chart-card">
          <h3>Balances by Location</h3>
          <div className="donut-container">
            <DonutChart 
              data={stats.byLocation} 
              colors={EXCHANGE_COLORS} 
              size={200}
              title="Total Portfolio"
              showTimestamp={true}
            />
          </div>
          <div className="chart-legend">
            {/* Wallets first - sorted by isIntermediary */}
            {stats.treasury.map((wallet) => (
              <div key={wallet.address || wallet.name} className="legend-item">
                <span className="legend-dot" style={{ backgroundColor: EXCHANGE_COLORS.treasury }} />
                <span className="legend-label" title={wallet.address || ''}>
                  {wallet.name}
                  {wallet.address && (
                    <span className="address-tag">
                      {shortenAddress(wallet.address)}
                    </span>
                  )}
                </span>
                <span className="legend-value mono">{formatCurrency(wallet.total)}</span>
              </div>
            ))}
            {/* Exchanges */}
            {stats.exchanges.slice(0, 5).map((exchange) => (
              <div key={exchange.id} className="legend-item">
                <span className="legend-dot" style={{ backgroundColor: exchange.color }} />
                <span className="legend-label">{exchange.name}</span>
                <span className="legend-value mono">{formatCurrency(exchange.total)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* By Asset */}
        <div className="chart-card">
          <h3>Balances by Asset</h3>
          <div className="donut-container">
            <DonutChart 
              data={stats.byAsset} 
              colors={ASSET_COLORS} 
              size={200}
              title="Total Portfolio"
              showTimestamp={true}
            />
          </div>
          <div className="chart-legend">
            {stats.byAsset.map((asset) => (
              <div key={asset.name} className="legend-item">
                <span className="legend-dot" style={{ backgroundColor: ASSET_COLORS[asset.name] || '#666' }} />
                <span className="legend-label">{asset.name}</span>
                <span className="legend-value mono">{formatCurrency(asset.value)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Daily P&L - only show if data exists */}
        {dailyPnL.length > 0 && (
          <div className="chart-card">
            <h3>Daily P&L (7 days)</h3>
            <PnLBarChart data={dailyPnL} />
          </div>
        )}
      </div>

      {/* Three Column Row - Exchange/Wallet List, Breakdown, Transactions */}
      <div className="three-column-row">
        {/* Exchange and Wallet List - First */}
        <ConsolidatedListTile 
          exchanges={stats.exchanges}
          wallets={stats.treasury}
          exchangesTotal={stats.exchangesTotal}
          treasuryTotal={stats.treasuryTotal}
          onSelectItem={setSelectedItem}
        />

        {/* Balance Breakdown - Second */}
        <div className="chart-card">
          <h3>
            {selectedItem ? (
              <>
                {selectedItem.type === 'wallet' ? `${selectedItem.data.name || 'Wallet'} Breakdown` : `${selectedItem.data.name} Breakdown`}
                <button 
                  className="close-chart-btn" 
                  onClick={(e) => { e.stopPropagation(); setSelectedItem(null); }}
                  title="Close"
                >
                  ×
                </button>
              </>
            ) : (
              'Click on Exchange or Wallet'
            )}
          </h3>
          {selectedItem ? (
            selectedItem.type === 'wallet' ? (
              // Wallet: Show split by chain (ARB vs ETH)
              <div className="wallet-breakdown">
                <div className="chain-breakdown-container">
                  {['arbitrum', 'ethereum'].map((chain) => {
                    const chainAssets = selectedItem.data.assets.filter(a => a.chain === chain);
                    const chainTotal = chainAssets.reduce((sum, a) => sum + (a.usdValue || 0), 0);
                    const chainInfo = {
                      arbitrum: { short: 'ARB', color: '#28a0f0' },
                      ethereum: { short: 'ETH', color: '#627eea' },
                    }[chain] || { short: chain.toUpperCase(), color: '#666' };
                    
                    if (chainTotal === 0 && chainAssets.length === 0) return null;
                    
                    const chartData = chainAssets.map(a => ({
                      name: a.symbol,
                      value: a.usdValue || 0,
                    })).filter(d => d.value > 0);
                    
                    return (
                      <div key={chain} className="chain-breakdown">
                        <div className="donut-container">
                          {chartData.length > 0 ? (
                            <DonutChart 
                              data={chartData} 
                              colors={ASSET_COLORS} 
                              size={150}
                              title={chainInfo.short}
                              showTimestamp={false}
                            />
                          ) : (
                            <div className="empty-chart">No assets on {chainInfo.short}</div>
                          )}
                        </div>
                        <div className="chain-breakdown-legend">
                          {chainAssets.map((asset, i) => (
                            <div key={i} className="legend-item-small">
                              <span className="legend-dot" style={{ backgroundColor: ASSET_COLORS[asset.symbol] || '#666' }} />
                              <span className="legend-label">{asset.symbol}</span>
                              <span className="legend-value mono">{formatCurrency(asset.usdValue || 0)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              // Exchange: Show asset breakdown with account type grouping
              <ExchangeBreakdownTile 
                exchange={selectedItem.data}
                exchangeId={selectedItem.id}
              />
            )
          ) : (
            <div className="empty-chart-hint">
              <span>Click on an exchange or wallet above to see its breakdown</span>
            </div>
          )}
        </div>

        {/* Transaction History - Third */}
        <TransactionHistoryTile selectedItem={selectedItem} onClose={() => setSelectedItem(null)} />
      </div>

      {/* Balance History Chart - Moved to bottom */}
      <BalanceHistoryChart timeRange={timeRange} onRangeChange={setTimeRange} />
    </div>
  );
}

export default BalancesDashboard;
